const memory = new Map<string, string[]>();
const snapshots = new Map<string, Record<string, RecentSnapshot>>();
const MAX_RECENT = 8;
const STORAGE_PREFIX = 'zagru.command-palette.recent.';
const SNAPSHOT_PREFIX = 'zagru.command-palette.recent-items.';

/** What the palette keeps of a navigation command so it can be offered again without re-running a search. */
export interface RecentSnapshot {
  id: string;
  label: string;
  href: string;
  group?: string;
  description?: string;
  icon?: string;
}

function readJson(key: string): unknown {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / privacy errors */
  }
}

function readStorage(key: string): string[] | null {
  const parsed = readJson(STORAGE_PREFIX + key);
  return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : null;
}

/** Recently run command ids. `persistKey` mirrors the list into localStorage. */
export function getRecent(persistKey?: string): string[] {
  const key = persistKey ?? '__memory__';
  const mem = memory.get(key);
  if (mem) return mem;
  const stored = persistKey ? readStorage(persistKey) : null;
  const ids = stored ?? [];
  memory.set(key, ids);
  return ids;
}

export function pushRecent(id: string, persistKey?: string, max = MAX_RECENT): string[] {
  const key = persistKey ?? '__memory__';
  const next = [id, ...getRecent(persistKey).filter((x) => x !== id)].slice(0, max);
  memory.set(key, next);
  if (persistKey) writeJson(STORAGE_PREFIX + persistKey, next);
  // Snapshots of commands that fell out of the list are dropped with them.
  const snaps = getRecentSnapshots(persistKey);
  const kept = Object.fromEntries(Object.entries(snaps).filter(([sid]) => next.includes(sid)));
  if (Object.keys(kept).length !== Object.keys(snaps).length) saveSnapshots(persistKey, kept);
  return next;
}

/** Snapshots of recently run navigation commands, keyed by command id. */
export function getRecentSnapshots(persistKey?: string): Record<string, RecentSnapshot> {
  const key = persistKey ?? '__memory__';
  const mem = snapshots.get(key);
  if (mem) return mem;
  const stored = persistKey ? readJson(SNAPSHOT_PREFIX + persistKey) : null;
  const valid: Record<string, RecentSnapshot> = {};
  if (stored && typeof stored === 'object') {
    for (const [id, value] of Object.entries(stored as Record<string, unknown>)) {
      const v = value as Partial<RecentSnapshot> | null;
      if (v && typeof v.label === 'string' && typeof v.href === 'string' && v.href.startsWith('/')) {
        valid[id] = { id, label: v.label, href: v.href, group: v.group, description: v.description, icon: v.icon };
      }
    }
  }
  snapshots.set(key, valid);
  return valid;
}

function saveSnapshots(persistKey: string | undefined, value: Record<string, RecentSnapshot>) {
  snapshots.set(persistKey ?? '__memory__', value);
  if (persistKey) writeJson(SNAPSHOT_PREFIX + persistKey, value);
}

export function pushRecentSnapshot(snapshot: RecentSnapshot, persistKey?: string): void {
  saveSnapshots(persistKey, { ...getRecentSnapshots(persistKey), [snapshot.id]: snapshot });
}

export function clearRecent(persistKey?: string): void {
  const key = persistKey ?? '__memory__';
  memory.delete(key);
  snapshots.delete(key);
  if (persistKey) {
    try {
      localStorage.removeItem(STORAGE_PREFIX + persistKey);
      localStorage.removeItem(SNAPSHOT_PREFIX + persistKey);
    } catch {
      /* ignore */
    }
  }
}
